import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { navItems } from '../../data/nav';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useDelayedUnmount } from '../../hooks/useDelayedUnmount';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { Logo } from './Logo';
import styles from './MobileDrawer.module.css';

/**
 * Replaces MUI's Drawer: a slide-in dialog with a focus trap, body scroll lock
 * and Escape-to-close, animated with a CSS transform rather than a JS library.
 *
 * The desktop "About" megamenu becomes an inline expanding group here, fed by
 * the same navItems array so the two cannot drift apart.
 */
export function MobileDrawer({ open, onClose, isActive }) {
  const panelRef = useRef(null);
  const [expanded, setExpanded] = useState(null);
  const mounted = useDelayedUnmount(open);
  // Mount first, then flip the class on the next frame, or the browser has no
  // "before" state to transition from and the panel simply appears.
  const [shown, setShown] = useState(false);

  useBodyScrollLock(open);
  useFocusTrap(panelRef, open, onClose);

  useEffect(() => {
    if (!mounted) {
      setShown(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setShown(open));
    return () => cancelAnimationFrame(raf);
  }, [mounted, open]);

  if (!mounted) return null;

  return (
    <>
      <div
        className={cn(styles.scrim, shown && styles.scrimOpen)}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        className={cn(styles.panel, shown && styles.panelOpen)}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
      >
        <div className={styles.head}>
          <Logo size={26} />
          <button type="button" className={styles.close} aria-label="Close menu" onClick={onClose}>
            <Icon name="close" size={20} />
          </button>
        </div>

        <nav className={styles.list} aria-label="Main">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.label} className={cn(expanded === item.label && styles.expanded)}>
                <button
                  type="button"
                  className={styles.link}
                  aria-expanded={expanded === item.label}
                  onClick={() => setExpanded((v) => (v === item.label ? null : item.label))}
                >
                  {item.label}
                  <Icon name="chevron-down" size={18} className={styles.caret} />
                </button>

                <div className={cn(styles.sub, expanded === item.label && styles.subOpen)}>
                  <div className={styles.subInner}>
                    <div className={styles.subList}>
                      {item.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className={cn(styles.subLink, isActive?.(child.to) && styles.subActive)}
                          onClick={onClose}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className={cn(styles.link, isActive?.(item.to) && styles.active)}
                onClick={onClose}
              >
                {item.label}
              </Link>
            ),
          )}
        </nav>

        <div className={styles.foot}>
          <Button to="/contact" icon="arrow-right-circle" block onClick={onClose}>
            Contact us
          </Button>
        </div>
      </div>
    </>
  );
}
